/**
 * Inline-editable concept-group title, used as the `titleSlot` of the shared
 * {@link ProjectDetailHeader} on the draft (proposal) and created concept-group
 * views (OR-6125 review). Enter saves, Esc cancels.
 *
 * Presentational + parent-owned state: the parent decides what "save" does. Two
 * layouts behind {@link ConceptGroupNameInlineEditProps.hoverToEdit} — a pencil
 * beside a small field (legacy), or Jira's work-item pattern where the title itself
 * is the control and the editor keeps the title's typography.
 *
 * TODO: cleanup with research_assistant_gen_3 ff
 */

import CheckIcon from '@mui/icons-material/Check';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import { Box, CircularProgress, IconButton, TextField, Typography } from '@mui/material';
import type { TextFieldProps } from '@mui/material';
import type { KeyboardEvent } from 'react';
import { useIntl } from 'react-intl';

import { borderRadius } from '@verantos-dev/shared-ui-components/tokens';

export interface ConceptGroupNameInlineEditProps {
  /** Committed name shown when not editing. */
  name: string;
  /** Whether the inline editor is open. */
  isEditing: boolean;
  /** Draft text while editing. */
  editedName: string;
  /** Message id of a validation error (blocks save + shows as helper text). */
  errorMessageId?: string | null;
  /** When false, the title is read-only (no edit affordance). */
  isEditable: boolean;
  /** When true, the save control shows a spinner and is disabled (rename in flight). */
  isSaving?: boolean;
  /**
   * Jira's work-item title pattern: no edit icon, the whole title is the control.
   * TODO: cleanup with concept_group_rename ff
   */
  hoverToEdit?: boolean;
  /** Cap on the typed name; only applied under {@link hoverToEdit}. */
  maxLength?: number;
  /**
   * When false the commit control is disabled — the caller decides what counts as a
   * change. Defaults true: the pencil layout drives `name` and `editedName` from one
   * state, so dirtiness is not observable there.
   */
  canCommit?: boolean;
  onStartEdit: () => void;
  onChange: (value: string) => void;
  onSave: () => void;
  onCancel: () => void;
  /** Stable testid prefix, e.g. `cg-preview` / `concept-group-detail`. */
  testIdPrefix: string;
}

/** Shared by the read and edit states so the title never shifts between them. */
const TITLE_TYPOGRAPHY = { typography: 'h5', fontWeight: 400 } as const;

/** ``floatBottom`` pins the pair to the field's bottom-right instead of the flex flow. */
const CommitControls = ({
  testIdPrefix,
  isSaving,
  canSave,
  onSave,
  onCancel,
  floatBottom,
}: Pick<ConceptGroupNameInlineEditProps, 'testIdPrefix' | 'onSave' | 'onCancel'> & {
  isSaving: boolean;
  canSave: boolean;
  floatBottom?: number;
}) => {
  const { formatMessage } = useIntl();
  return (
    <Box
      sx={
        floatBottom === undefined
          ? { display: 'flex', gap: 1, marginTop: 1 }
          : { display: 'flex', gap: 1, position: 'absolute', right: 1, bottom: floatBottom }
      }
    >
      <IconButton
        data-testid={`${testIdPrefix}-name-save`}
        size="small"
        onClick={onSave}
        disabled={!canSave || isSaving}
        color="primary"
        aria-label={formatMessage({ id: 'epiChat.conceptGroupName.saveAria' })}
      >
        {isSaving ? <CircularProgress size={16} color="inherit" /> : <CheckIcon fontSize="small" />}
      </IconButton>
      <IconButton
        data-testid={`${testIdPrefix}-name-cancel`}
        size="small"
        onClick={onCancel}
        disabled={isSaving}
        aria-label={formatMessage({ id: 'epiChat.conceptGroupName.cancelAria' })}
      >
        <CloseIcon fontSize="small" />
      </IconButton>
    </Box>
  );
};

export const ConceptGroupNameInlineEdit = ({
  name,
  isEditing,
  editedName,
  errorMessageId,
  isEditable,
  isSaving = false,
  hoverToEdit = false,
  maxLength,
  canCommit = true,
  onStartEdit,
  onChange,
  onSave,
  onCancel,
  testIdPrefix,
}: ConceptGroupNameInlineEditProps) => {
  const { formatMessage } = useIntl();
  const errorText = errorMessageId ? formatMessage({ id: errorMessageId }) : undefined;
  const canSave = canCommit && !errorText;

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && canSave) onSave();
    if (e.key === 'Escape') onCancel();
  };
  const controls = (floatBottom?: number) => (
    <CommitControls
      testIdPrefix={testIdPrefix}
      isSaving={isSaving}
      canSave={canSave}
      onSave={onSave}
      onCancel={onCancel}
      floatBottom={floatBottom}
    />
  );
  const nameField = (extra: TextFieldProps) => (
    <TextField
      data-testid={`${testIdPrefix}-name-input`}
      value={editedName}
      onChange={(e) => onChange(e.target.value)}
      onKeyDown={handleKeyDown}
      error={Boolean(errorText)}
      helperText={errorText}
      autoFocus
      disabled={isSaving}
      {...extra}
    />
  );

  if (isEditing && hoverToEdit) {
    // Tracks the name rather than filling the header: the text, plus room for the
    // controls and a few characters of slack, clamped usable-short to sane-long.
    const widthCh = Math.min(Math.max(editedName.length + 9, 16), 46);
    // The controls float at the field's bottom-right, overlaying the row beneath so
    // opening the editor neither resizes nor reflows the header.
    return (
      <Box
        data-testid={`${testIdPrefix}-name-edit`}
        // Title typography on the box too, so `ch` resolves in the font the text uses.
        sx={{
          ...TITLE_TYPOGRAPHY,
          position: 'relative',
          minWidth: 0,
          width: `${widthCh}ch`,
          maxWidth: '100%',
        }}
      >
        {nameField({
          multiline: true,
          fullWidth: true,
          inputProps: {
            maxLength,
            'aria-label': formatMessage({ id: 'epiChat.conceptGroupName.editAria' }),
          },
          sx: {
            '& .MuiInputBase-root': { ...TITLE_TYPOGRAPHY, paddingRight: 11 },
            '& .MuiInputBase-input': TITLE_TYPOGRAPHY,
          },
        })}
        {/* Clear of the helper text, which renders below the field's box. */}
        {controls(errorText ? 6 : 1)}
      </Box>
    );
  }

  if (isEditing) {
    return (
      <Box
        data-testid={`${testIdPrefix}-name-edit`}
        sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flex: 1, minWidth: 0 }}
      >
        {nameField({ size: 'small', sx: { flex: 1, minWidth: 0, maxWidth: 480 } })}
        {controls()}
      </Box>
    );
  }

  if (hoverToEdit) {
    const startEditOnKey = (e: KeyboardEvent<HTMLElement>) => {
      if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        onStartEdit();
      }
    };
    return (
      <Box
        data-testid={`${testIdPrefix}-name-view`}
        // Sizes to the name, so the status chip stays beside it rather than being
        // pushed out to the header's right edge.
        sx={{ display: 'flex', minWidth: 0, maxWidth: 640 }}
      >
        <Typography
          component="h1"
          data-testid={`${testIdPrefix}-name`}
          tabIndex={isEditable ? 0 : undefined}
          role={isEditable ? 'button' : undefined}
          aria-label={
            isEditable ? formatMessage({ id: 'epiChat.conceptGroupName.editAria' }) : undefined
          }
          onClick={isEditable ? onStartEdit : undefined}
          onKeyDown={isEditable ? startEditOnKey : undefined}
          sx={(theme) => ({
            ...TITLE_TYPOGRAPHY,
            minWidth: 0,
            overflowWrap: 'anywhere',
            // Negative margins cancel the padding in layout, so the hover outline has
            // breathing room while the title and the chip sit exactly where they did.
            paddingX: 2,
            paddingY: 1,
            marginX: -2,
            borderRadius: `${borderRadius.sm}px`,
            border: '1px solid transparent',
            cursor: isEditable ? 'text' : 'default',
            ...(isEditable && {
              '&:hover, &:focus-visible': { borderColor: theme.palette.divider },
              // Touch has no hover, so the affordance has to be permanent there.
              '@media (hover: none)': { borderColor: theme.palette.divider },
            }),
          })}
        >
          {name}
        </Typography>
      </Box>
    );
  }

  return (
    <Box
      data-testid={`${testIdPrefix}-name-view`}
      sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.5, minWidth: 0 }}
    >
      <Typography
        variant="h5"
        component="h1"
        data-testid={`${testIdPrefix}-name`}
        onClick={isEditable ? onStartEdit : undefined}
        sx={{
          fontWeight: 400,
          minWidth: 0,
          overflowWrap: 'anywhere',
          cursor: isEditable ? 'text' : 'default',
        }}
      >
        {name}
      </Typography>
      {isEditable ? (
        <IconButton
          data-testid={`${testIdPrefix}-name-edit-button`}
          size="small"
          onClick={onStartEdit}
          aria-label={formatMessage({ id: 'epiChat.conceptGroupName.editAria' })}
        >
          <EditIcon fontSize="small" />
        </IconButton>
      ) : null}
    </Box>
  );
};
